import React, { useState, useEffect, useCallback } from 'react';
import { repairService, customerService } from '../services/api';
import { GlassPanel, GlassButton, GlassCard, GlassInput, GlassSelect, PageHeader } from '../components/GlassUI';
import StatusBadge from '../components/StatusBadge';
import PhotoUpload from '../components/PhotoUpload';
import PaymentsPanel, { PaymentSummary } from '../components/PaymentsPanel';
import {
  FaPlus, FaTimes, FaCalendar, FaRupeeSign, FaUser, FaSearch, FaCheck, FaTools
} from 'react-icons/fa';

interface RepairsPageProps {
  onNavigate?: (page: string, id?: string) => void;
}

const REPAIR_STAGE_TABS = [
  { key: 'all', label: 'All' },
  { key: 'INWARD', label: 'Inward' },
  { key: 'KARIGAR_ROUTING', label: 'Karigar' },
  { key: 'QC_CHECK', label: 'QC' },
  { key: 'DELIVERY', label: 'Delivery' },
];

const PayChips: React.FC<{ summary?: any; className?: string }> = ({ summary, className = '' }) => {
  if (!summary || (summary.paidCount || 0) + (summary.unpaidCount || 0) === 0) return null;
  return (
    <span className={`inline-flex items-center gap-1 flex-wrap ${className}`}>
      {summary.totalPaid > 0 && (
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
          PAID ₹{Number(summary.totalPaid).toLocaleString('en-IN')}
        </span>
      )}
      {summary.totalUnpaid > 0 && (
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
          DUE ₹{Number(summary.totalUnpaid).toLocaleString('en-IN')}
        </span>
      )}
      {summary.totalPaid > 0 && summary.totalUnpaid === 0 && (
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white">SETTLED</span>
      )}
    </span>
  );
};

export const RepairsPage: React.FC<RepairsPageProps> = ({ onNavigate }) => {
  const [repairs, setRepairs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [activeStage, setActiveStage] = useState('all');

  // Create Repair Modal
  const [showCreate, setShowCreate] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [phoneLookup, setPhoneLookup] = useState('');
  const [lookupResult, setLookupResult] = useState<any>(null);
  const [lookupAttempted, setLookupAttempted] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [createForm, setCreateForm] = useState({
    customerId: '',
    customerName: '',
    itemDescription: '',
    problemDescription: '',
    photos: [] as string[],
    receivedWeight: '',
    estimatedCost: '',
    advanceAmount: '',
    paymentMethod: 'CASH',
    notes: '',
  });

  // Detail Modal
  const [selectedRepair, setSelectedRepair] = useState<any>(null);
  const [repairManualSummary, setRepairManualSummary] = useState<PaymentSummary | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [stageForm, setStageForm] = useState({
    karigarName: '',
    promisedDate: '',
    instructions: '',
    repairedPhotos: [] as string[],
    finalWeight: '',
    extraGold: '',
    extraStones: '',
    finalCharge: '',
    paymentMethod: 'CASH',
    paymentAmount: '',
    confirmHandover: false,
  });

  const loadRepairs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await repairService.getAll(
        undefined,
        activeStage === 'all' ? undefined : activeStage,
        page,
        20
      );
      setRepairs(response.data.repairs || []);
      setTotal(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Error loading repairs:', error);
    } finally {
      setLoading(false);
    }
  }, [page, activeStage]);

  useEffect(() => {
    loadRepairs();
  }, [loadRepairs]);

  const handlePhoneLookup = async () => {
    if (!phoneLookup.trim()) return;
    setLookupLoading(true);
    setLookupAttempted(true);
    try {
      const res = await customerService.getByPhone(phoneLookup.trim());
      const found = res.data.customer || res.data || null;
      setLookupResult(found);
      if (found) {
        setCreateForm(prev => ({
          ...prev,
          customerId: found.id || '',
          customerName: prev.customerName || found.customerName || '',
        }));
      } else {
        setCreateForm(prev => ({ ...prev, customerId: '' }));
      }
    } catch {
      setLookupResult(null);
      setCreateForm(prev => ({ ...prev, customerId: '' }));
    } finally {
      setLookupLoading(false);
    }
  };

  const handleCreateRepair = async () => {
    if (!phoneLookup.trim()) {
      alert('Enter the customer phone number');
      return;
    }
    if (!createForm.itemDescription.trim()) {
      alert('Item description is required');
      return;
    }
    if (!createForm.customerId && !createForm.customerName.trim()) {
      alert('This is a new customer — please enter the customer name');
      return;
    }
    setCreateLoading(true);
    try {
      let customerId = createForm.customerId;

      if (!customerId) {
        const custRes = await customerService.createOrUpdate({
          customerName: createForm.customerName.trim(),
          cellNumber: phoneLookup.trim(),
          sourceType: 'NEW',
        });
        customerId = custRes.data.customer?.id;
      }

      if (!customerId) {
        throw new Error('Could not resolve customer');
      }

      const created = await repairService.create({
        customerId,
        itemDescription: createForm.itemDescription,
        problemDescription: createForm.problemDescription || createForm.itemDescription,
        originalPhoto: createForm.photos[0] || undefined,
        receivedWeight: Number(createForm.receivedWeight) || undefined,
        estimatedCost: Number(createForm.estimatedCost) || undefined,
        notes: createForm.notes,
      });

      const repairId = created.data?.repair?.id;
      const advance = Number(createForm.advanceAmount) || 0;
      if (repairId && advance > 0) {
        try {
          await repairService.addPayment(repairId, {
            amount: advance,
            kind: 'ADVANCE',
            status: 'PAID',
            method: createForm.paymentMethod,
            note: 'Advance taken at ticket creation',
          });
        } catch (payErr) {
          console.error('Advance save failed:', payErr);
        }
      }

      alert('Repair ticket created successfully');
      setShowCreate(false);
      resetCreateForm();
      loadRepairs();
    } catch (err: any) {
      alert(err.response?.data?.error || err.message || 'Failed to create repair');
    } finally {
      setCreateLoading(false);
    }
  };

  const resetCreateForm = () => {
    setPhoneLookup('');
    setLookupResult(null);
    setLookupAttempted(false);
    setCreateForm({
      customerId: '',
      customerName: '',
      itemDescription: '',
      problemDescription: '',
      photos: [],
      receivedWeight: '',
      estimatedCost: '',
      advanceAmount: '',
      paymentMethod: 'CASH',
      notes: '',
    });
  };

  const openDetail = async (repair: any) => {
    setSelectedRepair(repair);
    setRepairManualSummary(null);
    setDetailLoading(true);
    try {
      const res = await repairService.getById(repair.id);
      setSelectedRepair(res.data.repair || res.data);
    } catch {
      // Use existing data
    } finally {
      setDetailLoading(false);
    }
    // Reset stage form
    setStageForm({
      karigarName: '',
      promisedDate: '',
      instructions: '',
      repairedPhotos: [],
      finalWeight: '',
      extraGold: '',
      extraStones: '',
      finalCharge: '',
      paymentMethod: 'CASH',
      paymentAmount: '',
      confirmHandover: false,
    });
  };

  const handleStageUpdate = async (nextStage: string) => {
    if (!selectedRepair) return;
    setDetailLoading(true);
    try {
      let notes = '';
      let photos: string[] = [];

      if (nextStage === 'KARIGAR_ROUTING') {
        notes = `Karigar: ${stageForm.karigarName}. Promise: ${stageForm.promisedDate}. ${stageForm.instructions}`;
      } else if (nextStage === 'QC_CHECK') {
        notes = `Final weight: ${stageForm.finalWeight}g. Extra gold: ${stageForm.extraGold}g. Extra stones: ${stageForm.extraStones}. Final charge: ₹${stageForm.finalCharge}`;
        photos = stageForm.repairedPhotos;
      } else if (nextStage === 'DELIVERY') {
        notes = `Payment: ${stageForm.paymentMethod} ₹${stageForm.paymentAmount}`;
      }

      await repairService.updateStage(selectedRepair.id, {
        stage: nextStage,
        notes,
        photos,
        finalCost: stageForm.finalCharge ? Number(stageForm.finalCharge) : undefined,
      });

      // Reload the repair detail
      const res = await repairService.getById(selectedRepair.id);
      setSelectedRepair(res.data.repair || res.data);
      alert(`Repair moved to ${nextStage.replace(/_/g, ' ')}`);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update stage');
    } finally {
      setDetailLoading(false);
    }
  };

  const getStageIndex = (stage: string): number => {
    const stages = ['INWARD', 'KARIGAR_ROUTING', 'QC_CHECK', 'DELIVERY'];
    return stages.indexOf(stage);
  };

  const STAGES_4 = [
    { key: 'INWARD', label: 'Inward', num: 1 },
    { key: 'KARIGAR_ROUTING', label: 'Karigar', num: 2 },
    { key: 'QC_CHECK', label: 'QC', num: 3 },
    { key: 'DELIVERY', label: 'Delivery', num: 4 },
  ];

  const updateStageForm = (field: string, value: any) => {
    setStageForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <PageHeader
        title="Repairs"
        subtitle={`${total} total repairs`}
        icon={<FaTools />}
        action={
          <GlassButton variant="primary" onClick={() => { resetCreateForm(); setShowCreate(true); }} className="w-full sm:w-auto">
            <FaPlus /> New Repair
          </GlassButton>
        }
      />

      {/* Stage Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {REPAIR_STAGE_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => { setActiveStage(tab.key); setPage(1); }}
            className={`px-4 py-2 rounded-lg font-semibold transition-all border whitespace-nowrap text-sm ${
              activeStage === tab.key
                ? 'bg-slate-900 border-slate-900 text-white'
                : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-slate-300 border-t-slate-900 rounded-full" />
        </div>
      )}

      {/* Repair Cards Grid */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {repairs.map((repair: any) => (
            <GlassCard
              key={repair.id}
              className="hover:shadow-xl transition-shadow cursor-pointer"
              onClick={() => openDetail(repair)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-slate-900 text-sm">{repair.repairTicket}</p>
                  <p className="text-slate-500 text-xs">{repair.customer?.customerName || 'N/A'}</p>
                </div>
                <StatusBadge
                  stage={repair.currentStage || repair.repairStatus}
                  type="repair"
                  className="text-xs shrink-0 ml-2"
                />
              </div>

              <p className="text-slate-700 text-sm mb-3 line-clamp-2">{repair.itemDescription}</p>

              <div className="space-y-1 text-xs text-slate-600">
                <p className="flex items-center gap-1">
                  <FaCalendar size={10} /> Drop-off: {new Date(repair.dropOffDate || repair.createdAt).toLocaleDateString()}
                </p>
                {repair.estimatedCost && (
                  <p className="flex items-center gap-1 text-amber-600 font-semibold">
                    <FaRupeeSign size={10} /> Est. ₹{repair.estimatedCost.toFixed(2)}
                  </p>
                )}
                {repair.finalCost && (
                  <p className="flex items-center gap-1 text-green-600 font-semibold">
                    <FaRupeeSign size={10} /> Final ₹{repair.finalCost.toFixed(2)}
                  </p>
                )}
              </div>
              <PayChips summary={repair.paymentSummary} className="mt-2" />
            </GlassCard>
          ))}
        </div>
      )}

      {!loading && repairs.length === 0 && (
        <GlassCard className="text-center py-12">
          <p className="text-slate-400 text-lg">No repairs found</p>
        </GlassCard>
      )}

      {/* Pagination */}
      {total > 20 && (
        <div className="flex justify-center items-center gap-2 mt-8">
          <GlassButton
            onClick={() => setPage(Math.max(1, page - 1))}
            variant="secondary"
            disabled={page === 1}
          >
            Previous
          </GlassButton>
          <GlassCard className="px-4 py-2 text-slate-900 text-sm">
            Page {page} of {Math.ceil(total / 20)}
          </GlassCard>
          <GlassButton
            onClick={() => setPage(page + 1)}
            variant="secondary"
            disabled={page * 20 >= total}
          >
            Next
          </GlassButton>
        </div>
      )}

      {/* Create Repair Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowCreate(false)}>
          <GlassPanel className="max-w-lg w-full max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-xl font-bold text-slate-900">Create Repair Ticket</h2>
              <button onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-slate-600">
                <FaTimes size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Phone Lookup */}
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">Customer Phone *</label>
                <div className="flex gap-2">
                  <GlassInput
                    type="tel"
                    value={phoneLookup}
                    onChange={e => setPhoneLookup(e.target.value)}
                    placeholder="Enter phone number"
                    onKeyDown={e => e.key === 'Enter' && handlePhoneLookup()}
                  />
                  <GlassButton
                    variant="primary"
                    className="shrink-0"
                    onClick={handlePhoneLookup}
                    disabled={lookupLoading}
                  >
                    <FaSearch />
                  </GlassButton>
                </div>
                {lookupResult && (
                  <GlassCard className="mt-2 bg-green-50 border-green-200">
                    <p className="text-green-800 font-semibold text-sm">{lookupResult.customerName || lookupResult.name}</p>
                    <p className="text-green-600 text-xs">{lookupResult.cellNumber}</p>
                  </GlassCard>
                )}
                {lookupLoading && <p className="text-xs text-slate-400 mt-1">Looking up...</p>}
                {phoneLookup && !lookupLoading && lookupAttempted && !lookupResult && (
                  <p className="text-xs text-amber-600 mt-1">No customer found. Will be created — enter the name below.</p>
                )}

                {phoneLookup && !lookupLoading && lookupAttempted && !lookupResult && (
                  <div className="mt-3">
                    <label className="block text-slate-700 font-semibold mb-1 text-sm">Customer Name *</label>
                    <GlassInput
                      value={createForm.customerName}
                      onChange={e => setCreateForm(prev => ({ ...prev, customerName: e.target.value }))}
                      placeholder="Full name"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">Item Description *</label>
                <GlassInput
                  value={createForm.itemDescription}
                  onChange={e => setCreateForm(prev => ({ ...prev, itemDescription: e.target.value }))}
                  placeholder="e.g., Gold ring, silver bracelet..."
                />
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">Problem Description</label>
                <textarea
                  value={createForm.problemDescription}
                  onChange={e => setCreateForm(prev => ({ ...prev, problemDescription: e.target.value }))}
                  placeholder="Describe the issue..."
                  className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-500 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 transition-all"
                  rows={3}
                />
              </div>

              <PhotoUpload
                photos={createForm.photos}
                onPhotosChange={(photos) => setCreateForm(prev => ({ ...prev, photos }))}
                capture="environment"
                label="Original Item Photo"
              />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1 text-sm">Received Weight (g)</label>
                  <GlassInput
                    type="number"
                    step="0.01"
                    value={createForm.receivedWeight}
                    onChange={e => setCreateForm(prev => ({ ...prev, receivedWeight: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1 text-sm">Estimated Cost (₹)</label>
                  <GlassInput
                    type="number"
                    step="0.01"
                    value={createForm.estimatedCost}
                    onChange={e => setCreateForm(prev => ({ ...prev, estimatedCost: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Advance / balance manual entry */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1 text-sm">Advance Taken (₹)</label>
                  <GlassInput
                    type="number"
                    step="0.01"
                    min="0"
                    value={createForm.advanceAmount}
                    onChange={e => setCreateForm(prev => ({ ...prev, advanceAmount: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1 text-sm">Advance Method</label>
                  <GlassSelect
                    value={createForm.paymentMethod}
                    onChange={e => setCreateForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                  >
                    <option value="CASH">Cash</option>
                    <option value="UPI">UPI</option>
                    <option value="CARD">Card</option>
                    <option value="CHEQUE">Cheque</option>
                    <option value="ONLINE">Online Transfer</option>
                    <option value="BANK_TRANSFER">Bank Transfer</option>
                    <option value="OTHER">Other</option>
                  </GlassSelect>
                </div>
              </div>

              {(Number(createForm.estimatedCost) > 0 || Number(createForm.advanceAmount) > 0) && (
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Total</p>
                    <p className="font-bold text-slate-800">₹{(Number(createForm.estimatedCost) || 0).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Advance</p>
                    <p className="font-bold text-emerald-700">₹{(Number(createForm.advanceAmount) || 0).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-red-700">Balance To Pay</p>
                    <p className="font-bold text-red-700">
                      ₹{Math.max(0, (Number(createForm.estimatedCost) || 0) - (Number(createForm.advanceAmount) || 0)).toFixed(2)}
                    </p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">Notes</label>
                <textarea
                  value={createForm.notes}
                  onChange={e => setCreateForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional notes..."
                  className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-500 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 transition-all"
                  rows={2}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <GlassButton variant="secondary" className="flex-1" onClick={() => setShowCreate(false)}>
                Cancel
              </GlassButton>
              <GlassButton
                variant="primary"
                className="flex-1"
                onClick={handleCreateRepair}
                disabled={createLoading}
              >
                {createLoading ? 'Creating...' : 'Create Repair'}
              </GlassButton>
            </div>
          </GlassPanel>
        </div>
      )}

      {/* Repair Detail Modal */}
      {selectedRepair && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedRepair(null)}>
          <GlassPanel className="max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold text-slate-900">{selectedRepair.repairTicket}</h2>
                  <StatusBadge
                    stage={selectedRepair.currentStage || selectedRepair.repairStatus}
                    type="repair"
                  />
                </div>
                <p className="text-slate-500 text-sm">{selectedRepair.customer?.customerName || 'N/A'}</p>
              </div>
              <button onClick={() => { setSelectedRepair(null); setRepairManualSummary(null); }} className="text-slate-400 hover:text-slate-600">
                <FaTimes size={20} />
              </button>
            </div>

            {detailLoading && (
              <div className="flex justify-center py-8">
                <div className="animate-spin w-6 h-6 border-4 border-slate-300 border-t-slate-900 rounded-full" />
              </div>
            )}

            {/* Repair Info */}
            <GlassCard className="mb-4 bg-slate-50 border-slate-200">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-slate-500">Item</span>
                  <p className="font-semibold text-slate-900">{selectedRepair.itemDescription || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-slate-500">Problem</span>
                  <p className="font-semibold text-slate-900">{selectedRepair.issueDescription || selectedRepair.problemDescription || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-slate-500">Est. Cost</span>
                  <p className="font-semibold text-slate-900">₹{selectedRepair.estimatedCost?.toFixed(2) || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-slate-500">Drop-off Date</span>
                  <p className="font-semibold text-slate-900">
                    {new Date(selectedRepair.dropOffDate || selectedRepair.createdAt).toLocaleDateString()}
                  </p>
                </div>
                {selectedRepair.finalCost !== null && selectedRepair.finalCost !== undefined && (
                  <div>
                    <span className="text-slate-500">Final Cost</span>
                    <p className="font-semibold text-green-600">₹{selectedRepair.finalCost?.toFixed(2)}</p>
                  </div>
                )}
                {selectedRepair.weight && (
                  <div>
                    <span className="text-slate-500">Weight</span>
                    <p className="font-semibold text-slate-900">{selectedRepair.weight}g</p>
                  </div>
                )}
              </div>
            </GlassCard>

            {/* Manual payments / advances — records only */}
            <div className="mb-6">
              <PaymentsPanel
                kind="repair"
                recordId={selectedRepair.id}
                totalAmount={selectedRepair.finalRepairCharge ?? selectedRepair.finalCost ?? selectedRepair.estimatedCost ?? 0}
                onChange={(s: PaymentSummary) => setRepairManualSummary(s)}
              />
            </div>

            {/* Stage Progress Bar */}
            <div className="mb-6">
              <div className="flex items-center justify-between">
                {STAGES_4.map((stage, idx) => {
                  const currentIdx = getStageIndex(selectedRepair.currentStage || 'INWARD');
                  const isCompleted = idx < currentIdx;
                  const isCurrent = idx === currentIdx;
                  return (
                    <React.Fragment key={stage.key}>
                      <div className="flex flex-col items-center">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                            isCompleted
                              ? 'bg-green-500 text-white'
                              : isCurrent
                              ? 'bg-slate-900 text-white ring-4 ring-slate-200'
                              : 'bg-slate-200 text-slate-500'
                          }`}
                        >
                          {isCompleted ? <FaCheck size={12} /> : stage.num}
                        </div>
                        <span className={`text-xs mt-1 ${isCurrent ? 'font-bold text-slate-900' : 'text-slate-400'}`}>
                          {stage.label}
                        </span>
                      </div>
                      {idx < STAGES_4.length - 1 && (
                        <div className={`flex-1 h-1 mx-1 rounded ${idx < currentIdx ? 'bg-green-500' : 'bg-slate-200'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* Stage-Specific Forms */}
            {(selectedRepair.currentStage === 'INWARD' || !selectedRepair.currentStage) && (
              <GlassCard className="mb-4">
                <p className="text-slate-500 text-sm mb-2">Current Stage: Inward — Details captured at intake.</p>
                <GlassButton
                  variant="primary"
                  className="w-full"
                  onClick={() => handleStageUpdate('KARIGAR_ROUTING')}
                  disabled={detailLoading}
                >
                  Send to Karigar
                </GlassButton>
              </GlassCard>
            )}

            {selectedRepair.currentStage === 'KARIGAR_ROUTING' && (
              <GlassCard className="mb-4 space-y-3">
                <h3 className="font-bold text-slate-900">Send to Karigar</h3>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1 text-sm">
                    <FaUser className="inline mr-1" /> Karigar Name *
                  </label>
                  <GlassInput
                    value={stageForm.karigarName}
                    onChange={e => updateStageForm('karigarName', e.target.value)}
                    placeholder="Karigar name"
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1 text-sm">
                    <FaCalendar className="inline mr-1" /> Promised Date
                  </label>
                  <GlassInput
                    type="date"
                    value={stageForm.promisedDate}
                    onChange={e => updateStageForm('promisedDate', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-1 text-sm">Instructions</label>
                  <textarea
                    value={stageForm.instructions}
                    onChange={e => updateStageForm('instructions', e.target.value)}
                    placeholder="Special instructions..."
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:border-slate-400 transition-all"
                    rows={2}
                  />
                </div>
                <GlassButton
                  variant="primary"
                  className="w-full"
                  onClick={() => handleStageUpdate('QC_CHECK')}
                  disabled={detailLoading || !stageForm.karigarName.trim()}
                >
                  {detailLoading ? 'Updating...' : 'Assign & Move to QC'}
                </GlassButton>
              </GlassCard>
            )}

            {selectedRepair.currentStage === 'QC_CHECK' && (
              <GlassCard className="mb-4 space-y-3">
                <h3 className="font-bold text-slate-900">QC Check</h3>
                <PhotoUpload
                  photos={stageForm.repairedPhotos}
                  onPhotosChange={(photos) => updateStageForm('repairedPhotos', photos)}
                  capture="environment"
                  label="Repaired Item Photo"
                />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 text-sm">Final Weight (g)</label>
                    <GlassInput
                      type="number"
                      step="0.01"
                      value={stageForm.finalWeight}
                      onChange={e => updateStageForm('finalWeight', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 text-sm">Extra Gold (g)</label>
                    <GlassInput
                      type="number"
                      step="0.01"
                      value={stageForm.extraGold}
                      onChange={e => updateStageForm('extraGold', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 text-sm">Extra Stones</label>
                    <GlassInput
                      value={stageForm.extraStones}
                      onChange={e => updateStageForm('extraStones', e.target.value)}
                      placeholder="Details"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 text-sm">
                      <FaRupeeSign className="inline mr-1" /> Final Charge (₹)
                    </label>
                    <GlassInput
                      type="number"
                      step="0.01"
                      value={stageForm.finalCharge}
                      onChange={e => updateStageForm('finalCharge', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                </div>
                <GlassButton
                  variant="primary"
                  className="w-full"
                  onClick={() => handleStageUpdate('DELIVERY')}
                  disabled={detailLoading}
                >
                  {detailLoading ? 'Updating...' : 'QC Passed → Ready for Delivery'}
                </GlassButton>
              </GlassCard>
            )}

            {selectedRepair.currentStage === 'DELIVERY' && (
              <GlassCard className="mb-4 space-y-3">
                <h3 className="font-bold text-slate-900">Delivery & Payment</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 text-sm">Payment Method</label>
                    <GlassSelect
                      value={stageForm.paymentMethod}
                      onChange={e => updateStageForm('paymentMethod', e.target.value)}
                    >
                      <option value="CASH">Cash</option>
                      <option value="UPI">UPI</option>
                      <option value="CARD">Card</option>
                      <option value="ONLINE">Online Transfer</option>
                    </GlassSelect>
                  </div>
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 text-sm">
                      <FaRupeeSign className="inline mr-1" /> Amount (₹)
                    </label>
                    <GlassInput
                      type="number"
                      step="0.01"
                      value={stageForm.paymentAmount}
                      onChange={e => updateStageForm('paymentAmount', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={stageForm.confirmHandover}
                    onChange={e => updateStageForm('confirmHandover', e.target.checked)}
                    className="w-4 h-4 accent-slate-900"
                  />
                  <span className="text-sm text-slate-700 font-medium">Confirm item handed over to customer</span>
                </label>
                <GlassButton
                  variant="primary"
                  className="w-full bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    if (!stageForm.confirmHandover) {
                      alert('Please confirm the item was handed over');
                      return;
                    }
                    handleStageUpdate('DELIVERY');
                  }}
                  disabled={detailLoading}
                >
                  {detailLoading ? 'Completing...' : 'Complete Delivery'}
                </GlassButton>
              </GlassCard>
            )}

            <GlassButton variant="secondary" className="w-full" onClick={() => { setSelectedRepair(null); setRepairManualSummary(null); }}>
              Close
            </GlassButton>
          </GlassPanel>
        </div>
      )}
    </div>
  );
};

export default RepairsPage;
